import { Button } from '@/components/buttons/Button'
import { Checkbox, CheckboxGroup } from '@/components/Checkbox'
import { Col } from '@/components/Col'
import { Divider } from '@/components/Divider'
import { Form } from '@/components/Form'
import { FormItem } from '@/components/FormItem'
import { Input } from '@/components/inputs/Input'
import { TagInput } from '@/components/inputs/TagInput'
import { TextArea } from '@/components/inputs/TextArea'
import { errorMessage, successMessage } from '@/components/Message'
import { PageHeader } from '@/components/PageHeader'
import { RadioGroup } from '@/components/Radio'
import { Row } from '@/components/Row'
import { Select } from '@/components/selects/Select'
import { Space } from '@/components/Space'
import { Typography } from '@/components/Typography'

import { useForm } from '@/hooks/useForm'
import { useQueryParams } from '@/hooks/useQueryParams'
import { useWatch } from '@/hooks/useWatch'
import { useCreateNotification } from '@/queries/notifications/useCreateNotification'
import { useEditNotification } from '@/queries/notifications/useEditNotification'
import { useGetNotificationById } from '@/queries/notifications/useGetNotificationById'
import { useGetUsers } from '@/queries/users/useGetUsers'
import { PATHS } from '@/routes/PATHS'
import {
  type CreateNotificationProps,
  type NotificationProps,
  NotificationSource,
  ReportDataType,
} from '@/services/types/notifications/mockNotifications'
import { getShallowDiffBetweenObjects } from '@/utils/functions/helpers/getShallowDiffBetweenObjects'
import { replaceStringVars } from '@/utils/functions/i18n/replaceStringVars'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { CustomFilters } from './components/CustomFilters/CustomFilters'
import {
  type EmailReportFilterPropsUnion,
  EmailReportFilterSource,
} from './components/CustomFilters/FilterComponent'
import { IntervalSelectComponent } from './components/IntervalSelectComponent'
import { Skeleton } from './components/Skeleton'

import { UserNameWithEmail } from '@/components/Users/UserNameWithEmail/UserNameWithEmail'
import { UsersSearchSelect } from '@/components/Users/UsersSearchSelect'
import { emailReportsTrackingEvents } from '@/heap/emailReports/emailReportsTrackingEvents'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import styles from './styles.module.scss'

export enum EmailReportIntervalType {
  daily = 'daily',
  weekly = 'weekly',
  monthly = 'monthly',
}

export enum EmailReportFormatType {
  csv = 'csv',
  pdf = 'pdf',
}

type FieldNamePath = (string | number)[]
interface ValidateErrorEntity<Values = any> {
  values: Values
  errorFields: {
    name: FieldNamePath
    errors: string[]
  }[]
  outOfDate: boolean
}

interface FormValuesProps {
  intervalData: {
    interval: EmailReportIntervalType
    day: number | null
  }
  name: string
  description: string
  reportData: {
    reports: ReportDataType[]
    lookback: '30' | '60' | '90'
  }
  filters: EmailReportFilterPropsUnion[]
  emailContent: {
    isCustom: boolean
    bodyOverride: string
    subjectOverride: string
    selectedOutputs: EmailReportFormatType[]
  }
  recipients: {
    users?: string[]
    externalRecipients?: string[]
  }
  checkboxes?: {
    usersRecipients: boolean
    externalRecipients: boolean
  }
}

export const EMAIL_REPORT_TEST_ID = 'email-report-test-id'
export const I18N_PREFIX = 'pages.emailReport'

const REPORT_DATA_LOOKBACK_OPTIONS = ['30', '60', '90'].map((lookback) => ({
  label: lookback,
  value: lookback,
}))

const defaultValues: FormValuesProps = {
  intervalData: {
    interval: EmailReportIntervalType.weekly,
    day: 1,
  },
  name: '',
  description: '',
  reportData: {
    reports: [],
    lookback: '30',
  },
  filters: [],
  emailContent: {
    isCustom: false,
    bodyOverride: '',
    subjectOverride: '',
    selectedOutputs: [],
  },
  recipients: {
    users: [],
    externalRecipients: [],
  },
}

const FORMAT_OPTIONS = [EmailReportFormatType.csv, EmailReportFormatType.pdf].map((format) => ({
  label: format.toUpperCase(),
  value: format,
}))

const initialFormValues: FormValuesProps = {
  ...defaultValues,
  recipients: {
    ...defaultValues.recipients,
    users: [],
  },
}

function getFiltersFromNotification(
  filters: NotificationProps<NotificationSource.reports>['filters'],
): EmailReportFilterPropsUnion[] {
  return filters.map((filter) => {
    switch (filter.source) {
      case EmailReportFilterSource.location:
        return {
          source: EmailReportFilterSource.location,
          evaluation: filter.evaluation,
          stores: filter.stores.map((store) => store.storeId),
        }
      case EmailReportFilterSource.publisher:
        return {
          source: EmailReportFilterSource.publisher,
          evaluation: filter.evaluation,
          publishers: filter.publishers,
        }
      case EmailReportFilterSource.folder:
        return {
          source: EmailReportFilterSource.folder,
          folders: filter.folders.map((folder) => folder.folderId),
          evaluation: filter.evaluation,
        }
    }
  })
}

function parseNotificationData(
  notification: FormValuesProps,
): CreateNotificationProps<NotificationSource.reports> {
  const { recipients, intervalData, reportData, ...rest } = notification

  const parsedRecipients = {
    ...recipients,
    externalRecipients: recipients.externalRecipients?.map((email) => ({
      email,
    })),
  }

  return {
    ...rest,
    enabled: true,
    deliveryTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    deliveryTime: 800,
    deliveryDay: intervalData.day,
    interval: intervalData.interval,
    criteria: {
      source: NotificationSource.reports,
      reportCriteria: {
        lookback: Number(reportData.lookback),
        lookbackType: 'days',
        reports: reportData.reports,
      },
    },
    recipients: parsedRecipients,
  }
}

function getPatchNotificationData(
  originalNotificationData: FormValuesProps,
  updatedNotificationData: FormValuesProps,
): Partial<CreateNotificationProps<NotificationSource.reports>> {
  const parsedOriginalNotificationData = parseNotificationData(originalNotificationData)
  const parsedUpdatedNotificationData = parseNotificationData(updatedNotificationData)

  const diff = getShallowDiffBetweenObjects<CreateNotificationProps<NotificationSource.reports>>(
    parsedOriginalNotificationData,
    parsedUpdatedNotificationData,
  )

  const { interval, deliveryDay, deliveryTime, deliveryTimeZone } = parsedUpdatedNotificationData

  return {
    ...diff,
    interval,
    deliveryDay,
    deliveryTime,
    deliveryTimeZone,
  }
}

export function EmailReport() {
  const originalNotification = useRef<FormValuesProps>(initialFormValues)
  const [isEditFormValuesFilled, setIsEditFormValuesFilled] = useState(false)

  const { t } = useTranslation()
  const navigate = useNavigate()

  const queryParams = useQueryParams()
  const editNotificationId = queryParams.get('edit')

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )
  const showReviewAnalysis = hasReviewMonitoringFeature

  const getNotificationById = useGetNotificationById<NotificationSource.reports>({
    id: editNotificationId,
    notificationSource: NotificationSource.reports,
  })

  const createNotification = useCreateNotification(NotificationSource.reports)
  const editNotification = useEditNotification(NotificationSource.reports)

  const users = useGetUsers()
  const usersOptions = (users?.data?.users ?? []).map(({ id, ...user }) => ({
    label: <UserNameWithEmail user={user} />,
    value: id,
    selectedStateLabel: `${user.givenName} ${user.familyName}`,
  }))

  const form = useForm<FormValuesProps>()

  const watchIsEmailCustomValue = useWatch(['emailContent', 'isCustom'], form)
  const watchIntervalData = useWatch<FormValuesProps['intervalData']>('intervalData', form)
  const watchUsersRecipientsCheckbox = useWatch<boolean>(['checkboxes', 'usersRecipients'], form)
  const watchExternalRecipientsCheckbox = useWatch<boolean>(
    ['checkboxes', 'externalRecipients'],
    form,
  )
  const watchSelectedReports = useWatch<ReportDataType[]>(['reportData', 'reports'], form)

  const INTERVAL_OPTIONS = [
    EmailReportIntervalType.daily,
    EmailReportIntervalType.weekly,
    EmailReportIntervalType.monthly,
  ].map((interval) => ({
    label: t(`${I18N_PREFIX}.intervalLabels.${interval}`),
    value: interval,
  }))

  const REPORT_DATA_OPTIONS = [ReportDataType.analytics, ReportDataType.reviewAnalysis]

  const FILTERED_REPORT_DATA_OPTIONS = showReviewAnalysis
    ? REPORT_DATA_OPTIONS
    : REPORT_DATA_OPTIONS.filter((option) => option !== ReportDataType.reviewAnalysis)

  const MAPPED_REPORT_DATA_OPTIONS = FILTERED_REPORT_DATA_OPTIONS.map((report) => ({
    label: t(`${I18N_PREFIX}.form.sections.reportData.lookback.labels.${report}`),
    value: report,
  }))

  const getDefaultEmailContents = useCallback(
    (notificationInterval: EmailReportIntervalType) => {
      return {
        subjectOverride: t(
          `${I18N_PREFIX}.form.sections.emailContent.subject.defaultContents.${notificationInterval}`,
        ),
        bodyOverride: t(
          `${I18N_PREFIX}.form.sections.emailContent.body.defaultContents.${notificationInterval}`,
        ),
      }
    },
    [t],
  )

  const pageHeaderTitle = useMemo(() => {
    if (!editNotificationId) {
      return t(`${I18N_PREFIX}.title.create`)
    }

    if (!getNotificationById.data) {
      return t(`${I18N_PREFIX}.title.edit`)
    }

    return getNotificationById.data.notification.name
  }, [t, editNotificationId, getNotificationById.data])

  useEffect(() => {
    if (
      watchIsEmailCustomValue ||
      !watchIntervalData?.interval ||
      (Boolean(editNotificationId) && !isEditFormValuesFilled)
    )
      return

    const { subjectOverride, bodyOverride } = getDefaultEmailContents(watchIntervalData.interval)

    form.setFieldsValue({
      emailContent: {
        subjectOverride,
        bodyOverride,
      },
    })
  }, [
    editNotificationId,
    watchIntervalData,
    watchIsEmailCustomValue,
    form,
    getDefaultEmailContents,
    isEditFormValuesFilled,
  ])

  useEffect(() => {
    if (editNotificationId && !isEditFormValuesFilled) return

    if (watchSelectedReports?.length === 0) {
      form.setFieldsValue({
        filters: [],
      })
    }
  }, [editNotificationId, isEditFormValuesFilled, watchSelectedReports, form])

  useEffect(() => {
    if (!getNotificationById.data || !editNotificationId) return

    if (!isEditFormValuesFilled) {
      const {
        name,
        description,
        deliveryDay,
        interval,
        filters,
        channels: [{ isCustom, subjectOverride, bodyOverride, selectedOutputs }],
        recipients,
        criteria: { reportCriteria },
      } = getNotificationById.data.notification

      const defaultEmailContent = getDefaultEmailContents(interval)
      const parsedNotification: FormValuesProps = {
        name,
        description,
        intervalData: {
          day: deliveryDay,
          interval,
        },
        filters: getFiltersFromNotification(filters),
        reportData: {
          reports: reportCriteria.reports,
          lookback: `${reportCriteria.lookback}`,
        },
        emailContent: {
          isCustom,
          subjectOverride: isCustom ? subjectOverride : defaultEmailContent.subjectOverride,
          bodyOverride: isCustom ? bodyOverride : defaultEmailContent.bodyOverride,
          selectedOutputs,
        },
        recipients: {
          externalRecipients: recipients.externalRecipients.map(
            (externalRecipient) => externalRecipient.email,
          ),
          users: [],
        },
        checkboxes: {
          usersRecipients: !!recipients.users.length,
          externalRecipients: !!recipients.externalRecipients.length,
        },
      }

      originalNotification.current = parsedNotification
      form.setFieldsValue(parsedNotification)

      setIsEditFormValuesFilled(true)
    }

    if (!users.isPending) {
      const { users: notificationUsers } = getNotificationById.data.notification.recipients

      const parsedUsersRecipients = notificationUsers.map(({ userId }) => userId)

      form.setFieldValue(['recipients', 'users'], parsedUsersRecipients)

      originalNotification.current = {
        ...originalNotification.current,
        recipients: {
          ...originalNotification.current.recipients,
          users: parsedUsersRecipients,
        },
      }
    }
  }, [
    editNotificationId,
    getNotificationById.data,
    form,
    getDefaultEmailContents,
    users.isPending,
    isEditFormValuesFilled,
  ])

  useEffect(() => {
    if (typeof watchUsersRecipientsCheckbox === 'boolean' && !watchUsersRecipientsCheckbox) {
      form.setFields([
        {
          name: ['recipients', 'users'],
          errors: [],
          value: [],
        },
      ])
    }

    if (typeof watchExternalRecipientsCheckbox === 'boolean' && !watchExternalRecipientsCheckbox) {
      form.setFields([
        {
          name: ['recipients', 'externalRecipients'],
          errors: [],
          value: [],
        },
      ])
    }
  }, [form, watchUsersRecipientsCheckbox, watchExternalRecipientsCheckbox])

  function handleCancel() {
    navigate(PATHS.emailReportsPage, { replace: true })
  }

  function formSubmitFailed(errorInfo: ValidateErrorEntity<FormValuesProps>) {
    const { errorFields } = errorInfo

    const erroredElements = errorFields
      .map((error) => {
        return document.getElementById(error.name.join('_'))
      })
      .filter((element): element is HTMLElement => Boolean(element))
      .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)

    erroredElements.at(0)?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    })
  }

  async function formSubmit(values: FormValuesProps) {
    const { checkboxes: _checkboxes, ...formValues } = values

    if (editNotificationId) {
      const { checkboxes: _checkboxes, ...originalNotificationData } = originalNotification.current

      const patchNotificationData = getPatchNotificationData(originalNotificationData, formValues)

      try {
        await editNotification.mutateAsync({
          id: editNotificationId,
          data: patchNotificationData,
        })
        successMessage(
          replaceStringVars({
            text: t(`${I18N_PREFIX}.submit.success`),
            vars: ['updated'],
          }),
        )

        emailReportsTrackingEvents.edit({
          emailReportId: editNotificationId,
          status: 'success',
        })

        navigate(PATHS.emailReportsPage, { replace: true })
      } catch {
        errorMessage(t(`${I18N_PREFIX}.submit.error`))

        emailReportsTrackingEvents.edit({
          emailReportId: editNotificationId,
          status: 'error',
        })
      }

      return
    }

    try {
      const createdNotification = await createNotification.mutateAsync(
        parseNotificationData(formValues),
      )

      successMessage(
        replaceStringVars({
          text: t(`${I18N_PREFIX}.submit.success`),
          vars: ['created'],
        }),
      )

      emailReportsTrackingEvents.create({
        emailReportId: createdNotification.notification.id,
        status: 'success',
      })

      navigate(PATHS.emailReportsPage, { replace: true })
    } catch {
      errorMessage(t(`${I18N_PREFIX}.submit.error`))

      emailReportsTrackingEvents.create({
        status: 'error',
      })
    }
  }

  return (
    <div className={styles.container} data-testid={EMAIL_REPORT_TEST_ID}>
      <PageHeader title={pageHeaderTitle} fallbackUrl={PATHS.emailReportsPage} showBackArrow />

      <Row>
        {editNotificationId && getNotificationById.isPending ? (
          <Skeleton />
        ) : (
          <Col span={24}>
            <Form
              id="report-form"
              form={form}
              onFinish={formSubmit}
              onFinishFailed={formSubmitFailed}
              initialValues={initialFormValues}
              className={styles.form}
            >
              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.general.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.general.description`)}
                  </Typography.Text>
                </Col>

                <Col span={19}>
                  <Space direction="vertical" size={32}>
                    <Row gutter={8}>
                      <Col className={styles.frequencySection} span={24}>
                        <FormItem name={['intervalData']} noStyle>
                          <IntervalSelectComponent intervalOptions={INTERVAL_OPTIONS} />
                        </FormItem>
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24}>
                        <FormItem
                          requiredMark
                          validation="required"
                          name="name"
                          label={t(`${I18N_PREFIX}.form.sections.general.name.label`)}
                          className={styles.fullWidth}
                        >
                          <Input
                            placeholder={t(`${I18N_PREFIX}.form.sections.general.name.placeholder`)}
                          />
                        </FormItem>
                        <FormItem
                          requiredMark
                          validation="required"
                          name="description"
                          label={t(`${I18N_PREFIX}.form.sections.general.description.label`)}
                          className={styles.fullWidth}
                          tooltip={t(`${I18N_PREFIX}.form.sections.general.description.tooltip`)}
                        >
                          <TextArea
                            placeholder={t(
                              `${I18N_PREFIX}.form.sections.general.description.placeholder`,
                            )}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Space>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.reportData.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.reportData.description`)}
                  </Typography.Text>
                </Col>

                <Col span={19}>
                  <Row gutter={8}>
                    <FormItem
                      validation="emailReports.reportData.reports"
                      name={['reportData', 'reports']}
                    >
                      <CheckboxGroup options={MAPPED_REPORT_DATA_OPTIONS} />
                    </FormItem>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24} className={styles.reportDataLookbackSection}>
                      <Typography.Text>
                        {t(`${I18N_PREFIX}.form.sections.reportData.lookback.prefix`)}
                      </Typography.Text>

                      <FormItem noStyle name={['reportData', 'lookback']}>
                        <Select options={REPORT_DATA_LOOKBACK_OPTIONS} />
                      </FormItem>

                      <Typography.Text>
                        {t(`${I18N_PREFIX}.form.sections.reportData.lookback.suffix`)}
                      </Typography.Text>
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.filters.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.filters.description`)}
                  </Typography.Text>
                </Col>

                <Col span={19}>
                  <CustomFilters
                    name="filters"
                    disabled={watchSelectedReports?.length === 0}
                    selectedReports={watchSelectedReports}
                  />
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.format.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.format.description`)}
                  </Typography.Text>
                </Col>

                <Col span={19}>
                  <FormItem name={['emailContent', 'selectedOutputs']} validation="required">
                    <CheckboxGroup options={FORMAT_OPTIONS} />
                  </FormItem>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.emailContent.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.emailContent.description`)}
                  </Typography.Text>
                </Col>
                <Col span={19}>
                  <Space direction="vertical" className={styles.fullWidth}>
                    <FormItem name={['emailContent', 'isCustom']}>
                      <RadioGroup
                        options={[
                          {
                            label: t(`${I18N_PREFIX}.form.sections.emailContent.isCustom.default`),
                            value: false,
                          },
                          {
                            label: t(`${I18N_PREFIX}.form.sections.emailContent.isCustom.custom`),
                            value: true,
                          },
                        ]}
                      />
                    </FormItem>

                    <FormItem
                      name={['emailContent', 'subjectOverride']}
                      label={t(`${I18N_PREFIX}.form.sections.emailContent.subject.label`)}
                      validation="required"
                      requiredMark
                    >
                      <Input
                        disabled={!watchIsEmailCustomValue}
                        placeholder={t(
                          `${I18N_PREFIX}.form.sections.emailContent.subject.placeholder`,
                        )}
                      />
                    </FormItem>

                    <FormItem
                      name={['emailContent', 'bodyOverride']}
                      label={t(`${I18N_PREFIX}.form.sections.emailContent.body.label`)}
                      validation="required"
                      requiredMark
                    >
                      <TextArea
                        disabled={!watchIsEmailCustomValue}
                        placeholder={t(
                          `${I18N_PREFIX}.form.sections.emailContent.body.placeholder`,
                        )}
                        rows={6}
                      />
                    </FormItem>
                  </Space>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={5} className={styles.sectionInfo}>
                  <Typography.Title level={5}>
                    {t(`${I18N_PREFIX}.sections.recipients.title`)}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    {t(`${I18N_PREFIX}.sections.recipients.description`)}
                  </Typography.Text>
                </Col>

                <Col span={19}>
                  <Space direction="vertical" className={styles.fullWidth}>
                    <FormItem
                      name={['checkboxes', 'usersRecipients']}
                      valuePropName="checked"
                      noMargin
                    >
                      <Checkbox>
                        {t(`${I18N_PREFIX}.form.sections.recipients.specificUsers.checkboxLabel`)}
                      </Checkbox>
                    </FormItem>

                    <FormItem
                      name={['recipients', 'users']}
                      hidden={!watchUsersRecipientsCheckbox}
                      {...(watchUsersRecipientsCheckbox && {
                        validation: 'notifications.recipients.users',
                      })}
                    >
                      <UsersSearchSelect
                        options={usersOptions}
                        placeholder={
                          users.isPending
                            ? t('utils.loading')
                            : t(
                                `${I18N_PREFIX}.form.sections.recipients.specificUsers.selectPlaceholder`,
                              )
                        }
                        disabled={users.isPending}
                        countLabel={t('utils.users')}
                      />
                    </FormItem>

                    <FormItem
                      name={['checkboxes', 'externalRecipients']}
                      valuePropName="checked"
                      validation="notifications.recipients"
                      noMargin
                    >
                      <Checkbox>
                        {t(
                          `${I18N_PREFIX}.form.sections.recipients.externalRecipient.checkboxLabel`,
                        )}
                      </Checkbox>
                    </FormItem>

                    <FormItem
                      name={['recipients', 'externalRecipients']}
                      hidden={!watchExternalRecipientsCheckbox}
                      {...(watchExternalRecipientsCheckbox && {
                        validation: 'notifications.recipients.externalRecipients',
                      })}
                    >
                      <TagInput
                        placeholder={t(
                          `${I18N_PREFIX}.form.sections.recipients.externalRecipient.input.placeholder`,
                        )}
                        description={t(
                          `${I18N_PREFIX}.form.sections.recipients.externalRecipient.input.description`,
                        )}
                      />
                    </FormItem>
                  </Space>
                </Col>
              </Row>
            </Form>

            <Divider />

            <Space direction="vertical" align="end">
              <Space>
                <Button onClick={handleCancel}>{t(`${I18N_PREFIX}.buttons.cancel`)}</Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  form="report-form"
                  loading={createNotification.isPending || editNotification.isPending}
                >
                  {t(`${I18N_PREFIX}.buttons.save`)}
                </Button>
              </Space>
            </Space>
          </Col>
        )}
      </Row>
    </div>
  )
}
